import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Printer, ScanLine, Trash2 } from 'lucide-react';
import { XMAS_ADDRESS_STAGE_LABELS, XMAS_PACKAGE_LABELS, XMAS_PALLET_SIZE_LABELS, type XmasAddressDto, type XmasAddressStage, type XmasBatchDto, type XmasBoxDto, type XmasMyPrintersDto, type XmasPackageKind, type XmasPlanDto, type XmasPlanOptionsDto, type XmasPlanSummaryDto, type XmasScanResultDto, type XmasShipResultDto, type XmasStockLineDto } from '@thebb/shared';
import { del, get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';

type Size = keyof typeof XMAS_PALLET_SIZE_LABELS;
const SIZES = Object.keys(XMAS_PALLET_SIZE_LABELS) as Size[];
const KINDS = Object.keys(XMAS_PACKAGE_LABELS) as XmasPackageKind[];
const kg = (g: number) => `${(g / 1000).toLocaleString('da-DK', { maximumFractionDigits: 1 })} kg`;
const toGrams = (s: string) => Math.round(Number(s.replace(',', '.')) * 1000);

/** A scan field that keeps focus, sends on Enter, and clears itself. */
function ScanField({ placeholder, onScan, busy }: { placeholder: string; onScan: (code: string) => void; busy?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  const [v, setV] = useState('');
  useEffect(() => { if (!busy) ref.current?.focus(); }, [busy]);
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) onScan(v.trim());
        setV('');
      }}
    >
      <ScanLine className="h-5 w-5 text-muted" />
      <input ref={ref} className="input w-80 font-mono" placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)} />
      <button className="btn" disabled={busy}>OK</button>
    </form>
  );
}

type PackageRowsState = Record<XmasPackageKind, { count: string; kg: string }>;
const NO_ROWS: PackageRowsState = { full: { count: '', kg: '' }, half: { count: '', kg: '' }, quarter: { count: '', kg: '' }, parcel: { count: '', kg: '' } };

/** How many of each package kind, and what each weighs. */
function PackageRows({ rows, setRows, autoFocus }: { rows: PackageRowsState; setRows: (r: PackageRowsState) => void; autoFocus?: boolean }) {
  return (
    <table className="w-full">
      <thead><tr className="text-left text-xs text-muted"><th className="pb-1 font-medium"></th><th className="pb-1 font-medium">Antal</th><th className="pb-1 font-medium">Vægt pr. stk. (kg)</th></tr></thead>
      <tbody>
        {KINDS.map((k, i) => (
          <tr key={k}>
            <td className="py-1 pr-3 font-medium">{XMAS_PACKAGE_LABELS[k]}</td>
            <td className="py-1 pr-2"><input className="input" inputMode="numeric" autoFocus={autoFocus && i === 0} value={rows[k].count} onChange={(e) => setRows({ ...rows, [k]: { ...rows[k], count: e.target.value.replace(/\D/g, '') } })} /></td>
            <td className="py-1"><input className="input" inputMode="decimal" disabled={!Number(rows[k].count)} value={rows[k].kg} onChange={(e) => setRows({ ...rows, [k]: { ...rows[k], kg: e.target.value } })} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The pick list scanned: how many pallets and kolli were used, and what each weighs. Prints one label per package. */
function PackagesModal({ batch, onClose, onDone }: { batch: { id: string; number: number; code: string; department: string; where: string; orders: number; packed: number; boxes: number }; onClose: () => void; onDone: (d: XmasBatchDto) => void }) {
  const [rows, setRows] = useState<PackageRowsState>(NO_ROWS);
  const [error, setError] = useState<unknown>(null);
  const used = KINDS.filter((k) => Number(rows[k].count) > 0);
  const total = used.reduce((t, k) => t + Number(rows[k].count), 0);
  const valid = used.length > 0 && used.every((k) => toGrams(rows[k].kg) > 0);
  const save = useMutation({ mutationFn: () => post<XmasBatchDto>(`/xmas/batches/${batch.id}/register`, { rows: used.map((k) => ({ kind: k, count: Number(rows[k].count), weightG: toGrams(rows[k].kg) })) }), onSuccess: onDone, onError: setError });
  return (
    <Modal title={`Plukliste ${batch.code}: batch ${batch.number}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div><div className="font-semibold">{batch.department}</div><div className="text-muted">{batch.where}</div></div>
        <p>{batch.orders - batch.packed > 0 ? `Alle ${batch.orders} gaver markeres som pakket.` : `Alle ${batch.orders} gaver er pakket.`} Hvor mange har du brugt?</p>
        {batch.boxes > 0 && <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">Der er allerede registreret {batch.boxes} kasser på denne batch. Det, du angiver her, kommer oveni.</p>}
        <ErrorBox error={error} />
        <PackageRows rows={rows} setRows={setRows} autoFocus />
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose}>Annuller</button>
          <button className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Gemmer…' : total ? `Print ${total} labels` : 'Print labels'}</button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The rest pick list scanned: what was left for later goes into the department's packages, which get
 * their new weight, or into new packages (their labels print). Either marks the rest packed.
 */
function RestModal({ batchId, onClose, onDone }: { batchId: string; onClose: () => void; onDone: (d: XmasBatchDto) => void }) {
  const q = useQuery({ queryKey: ['xmas-batch', batchId], queryFn: () => get<XmasBatchDto>(`/xmas/batches/${batchId}`) });
  const [weights, setWeights] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<PackageRowsState>(NO_ROWS);
  const [error, setError] = useState<unknown>(null);
  const d = q.data;
  const open = (d?.boxList ?? []).filter((b) => !b.shipment);
  const changed = open.filter((b) => toGrams(weights[b.id] ?? '') > 0);
  const used = KINDS.filter((k) => Number(rows[k].count) > 0);
  const valid = (changed.length > 0 || used.length > 0) && used.every((k) => toGrams(rows[k].kg) > 0);
  const save = useMutation({
    mutationFn: () => post<XmasBatchDto>(`/xmas/batches/${batchId}/rest`, { weights: changed.map((b) => ({ boxId: b.id, weightG: toGrams(weights[b.id]!) })), rows: used.map((k) => ({ kind: k, count: Number(rows[k].count), weightG: toGrams(rows[k].kg) })) }),
    onSuccess: onDone,
    onError: setError,
  });
  if (!d) return <Modal title="Restplukliste" onClose={onClose}>{q.isLoading ? <Spinner /> : <ErrorBox error={q.error} />}</Modal>;
  return (
    <Modal title={`Restplukliste ${d.restCode}: batch ${d.number}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div><div className="font-semibold">{d.department}</div><div className="text-muted">Adresse {d.address.number}: {d.address.street1}, {d.address.zip} {d.address.city}</div></div>
        <ul className="rounded-lg bg-canvas p-2">{d.restPick.map((p) => <li key={p.sku} className="flex gap-2"><span className="w-8 text-right font-semibold tabular-nums">{p.quantity}</span><span className="flex-1">{p.title}{p.variant && ` · ${p.variant}`}</span><span className="font-mono text-xs text-muted">{p.sku}</span></li>)}</ul>
        <p>Resten markeres som pakket og trækkes fra lageret. Skriv den nye vægt på de kasser, du har lagt den i.</p>
        <ErrorBox error={error} />
        {open.length > 0 ? (
          <table className="w-full">
            <thead><tr className="text-left text-xs text-muted"><th className="pb-1 font-medium">Kasse</th><th className="pb-1 font-medium">Placering</th><th className="pb-1 font-medium">Vægt nu</th><th className="pb-1 font-medium">Ny vægt (kg)</th></tr></thead>
            <tbody>
              {open.map((b, i) => (
                <tr key={b.id}>
                  <td className="py-1 pr-2"><span className="font-mono text-xs">{b.code}</span> {XMAS_PACKAGE_LABELS[b.kind]}</td>
                  <td className="py-1 pr-2 font-mono text-xs">{b.place ?? '–'}</td>
                  <td className="py-1 pr-2 tabular-nums">{kg(b.weightG)}</td>
                  <td className="py-1"><input className="input" inputMode="decimal" autoFocus={i === 0} value={weights[b.id] ?? ''} onChange={(e) => setWeights({ ...weights, [b.id]: e.target.value })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">Afdelingens kasser er booket: læg resten i nye kasser.</p>}
        <div className="font-medium">Nye kasser{open.length > 0 && ' (hvis det ikke kan være i de gamle)'}</div>
        <PackageRows rows={rows} setRows={setRows} autoFocus={!open.length} />
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose}>Annuller</button>
          <button className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Gemmer…' : 'Resten er pakket'}</button>
        </div>
      </div>
    </Modal>
  );
}

type ScannedBox = NonNullable<XmasScanResultDto['box']>;

/** A package whose address waits for what was left for later: it cannot be booked yet, so it gets a place to stand. */
function WaitingModal({ box, onClose, onDone }: { box: ScannedBox; onClose: () => void; onDone: (place: string | null) => void }) {
  const [place, setPlace] = useState(box.place ?? '');
  const [error, setError] = useState<unknown>(null);
  const save = useMutation({ mutationFn: () => put(`/xmas/boxes/${box.id}/place`, { place: place.trim() || null }), onSuccess: () => onDone(place.trim().toUpperCase() || null), onError: setError });
  return (
    <Modal title={`${box.code} kan ikke bookes endnu`} onClose={onClose}>
      <form className="space-y-3 text-sm" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div><div className="font-semibold">{XMAS_PACKAGE_LABELS[box.kind]} · {box.department}</div><div className="text-muted">{box.where}</div></div>
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">{box.waiting}</p>
        <ErrorBox error={error} />
        <label className="block"><span className="label">Placering: scan lokationens stregkode eller skriv den</span><input className="input font-mono" autoFocus value={place} onChange={(e) => setPlace(e.target.value)} placeholder="fx GULV-A3" /></label>
        <div className="flex justify-end gap-2"><button type="button" className="btn" onClick={onClose}>Luk</button><button className="btn btn-primary" disabled={save.isPending}>Gem placering</button></div>
      </form>
    </Modal>
  );
}

/** A kolli scanned: which pallet size it stands on and whether it is the last, or sent alone. */
function KolliModal({ box, onClose, onDone }: { box: ScannedBox; onClose: () => void; onDone: (r: XmasShipResultDto) => void }) {
  const [size, setSize] = useState<Size>(box.openPallet?.size ?? 'full');
  const [error, setError] = useState<unknown>(null);
  const ship = useMutation({ mutationFn: (body: { mode: 'pallet' | 'alone'; size?: Size; last?: boolean }) => post<XmasShipResultDto>(`/xmas/boxes/${box.id}/ship`, body), onSuccess: onDone, onError: setError });
  return (
    <Modal title={`Kolli ${box.code}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div><div className="font-semibold">{box.department} · {kg(box.weightG)}</div><div className="text-muted">{box.where}</div></div>
        <ErrorBox error={error} />
        <p>{box.openPallet ? <>Adressen har en åben palle, <span className="font-mono">{box.openPallet.code}</span>, med {box.openPallet.boxes} kolli.</> : 'Adressen har ingen åben palle: en ny startes.'}</p>
        <label className="block"><span className="label">Hvilken pallestørrelse står det på?</span><select className="input" value={size} onChange={(e) => setSize(e.target.value as Size)}>{SIZES.map((s) => <option key={s} value={s}>{XMAS_PALLET_SIZE_LABELS[s]}</option>)}</select></label>
        <p className="font-medium">Er det det sidste kolli på pallen?</p>
        <div className="grid grid-cols-2 gap-2">
          <button className="btn" disabled={ship.isPending} onClick={() => ship.mutate({ mode: 'pallet', size, last: false })}>Nej, der kommer flere</button>
          <button className="btn btn-primary" disabled={ship.isPending} onClick={() => ship.mutate({ mode: 'pallet', size, last: true })}>Ja, book pallen og print label</button>
        </div>
        <div className="border-t border-line pt-3"><button className="text-primary hover:underline" disabled={ship.isPending} onClick={() => ship.mutate({ mode: 'alone' })}>Send det alene som kolli, uden palle</button></div>
      </div>
    </Modal>
  );
}

/** The printers at this user's own packing table. */
function MyPrinters() {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const q = useQuery({ queryKey: ['xmas-my-printers'], queryFn: () => get<XmasMyPrintersDto>('/xmas/my-printers') });
  const save = useMutation({ mutationFn: (p: XmasMyPrintersDto) => put<XmasMyPrintersDto>('/xmas/my-printers', { labelPrinterId: p.labelPrinterId, smallLabelPrinterId: p.smallLabelPrinterId, documentPrinterId: p.documentPrinterId }), onSuccess: (d) => { qc.setQueryData(['xmas-my-printers'], d); setError(null); }, onError: setError });
  const d = q.data;
  if (!d) return null;
  const fields = [['labelPrinterId', 'Labelprinter 102 × 191 mm (kasser og fragt)'], ['smallLabelPrinterId', 'Labelprinter 76 × 50 mm (medarbejderlabels)'], ['documentPrinterId', 'A4-printer']] as const;
  return (
    <div className="card space-y-2 text-sm">
      <div className="font-semibold">Mine printere</div>
      <ErrorBox error={error} />
      <div className="grid grid-cols-3 gap-2">
        {fields.map(([k, label]) => (
          <label key={k} className="block"><span className="label">{label}</span>
            <select className="input" value={d[k] ?? ''} onChange={(e) => save.mutate({ ...d, [k]: e.target.value || null })}>
              <option value="">Planens standard</option>
              {d.printers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
        ))}
      </div>
      <p className="text-xs text-muted">Gælder for dit login, så hvert pakkebord kan bruge sine egne printere. Alt du printer og scanner i julepakningen går hertil.</p>
    </div>
  );
}

/** The packing table's station: every Christmas code goes here, and the next scan can follow straight after. */
export function XmasScanPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [log, setLog] = useState<string[]>([]);
  const [batch, setBatch] = useState<NonNullable<XmasScanResultDto['batch']> | null>(null);
  const [kolli, setKolli] = useState<ScannedBox | null>(null);
  const [booked, setBooked] = useState<ScannedBox | null>(null);
  const [waiting, setWaiting] = useState<ScannedBox | null>(null);
  const [rest, setRest] = useState<string | null>(null);
  const note = (m: string) => { setLog((l) => [m, ...l].slice(0, 12)); setError(null); void qc.invalidateQueries({ queryKey: ['xmas-address'] }); };
  const ship = useMutation({ mutationFn: (id: string) => post<XmasShipResultDto>(`/xmas/boxes/${id}/ship`, { mode: 'package' }), onSuccess: (r) => note(r.message), onError: setError });
  const reprint = useMutation({ mutationFn: (id: string) => post(`/xmas/boxes/${id}/reprint-freight`), onSuccess: () => { note(`${booked?.code}: fragtlabel sendt til print igen`); setBooked(null); }, onError: (e) => { setError(e); setBooked(null); } });
  const cancel = useMutation({ mutationFn: (id: string) => post(`/xmas/boxes/${id}/cancel-booking`), onSuccess: () => { note(`${booked?.code}: bookingen er annulleret. Scan kassen igen for at booke den.`); setBooked(null); }, onError: (e) => { setError(e); setBooked(null); } });
  const scan = useMutation({
    mutationFn: (code: string) => post<XmasScanResultDto>('/xmas/scan', { code }),
    onSuccess: (r) => {
      setError(null);
      if (r.kind === 'batch') setBatch(r.batch);
      else if (r.kind === 'rest') setRest(r.batchId);
      else if (r.kind === 'box') {
        const b = r.box!;
        if (b.shipment) setBooked(b);
        else if (b.waiting) setWaiting(b);
        else if (b.kind === 'parcel') setKolli(b);
        else ship.mutate(b.id);
      } else if (r.kind === 'gift') void nav({ to: '/xmas/batch/$id', params: { id: r.batchId! } });
      else if (r.kind === 'pallet') void nav({ to: '/xmas/address/$id', params: { id: r.addressId! } });
      else setError(new Error(r.message));
    },
    onError: setError,
  });
  return (
    <div className="space-y-4">
      <PageTitle title="Scan stregkode" subtitle="Scan pluklisten, når en batch er pakket, og angiv paller og kolli. Scan derefter hver kasselabel for at booke og printe fragtlabel." />
      <MyPrinters />
      <ErrorBox error={error} />
      <div className="card"><ScanField placeholder="Scan plukliste (XL…), restplukliste (XR…) eller kasse (XB…)" onScan={(c) => scan.mutate(c)} busy={scan.isPending || ship.isPending || !!batch || !!kolli || !!booked || !!waiting || !!rest} /></div>
      {log.length > 0 && <ul className="card space-y-1 text-sm">{log.map((m, i) => <li key={`${i}-${m}`} className={i === 0 ? 'font-semibold' : 'text-muted'}>{m}</li>)}</ul>}
      {batch && <PackagesModal batch={batch} onClose={() => setBatch(null)} onDone={(d) => { note(`Batch ${d.number} (${d.department}): ${d.packed} af ${d.orders} gaver pakket, labels sendt til print`); setBatch(null); }} />}
      {waiting && <WaitingModal box={waiting} onClose={() => setWaiting(null)} onDone={(place) => { note(`${waiting.code}: ${place ? `placeret på ${place}` : 'uden placering'}, venter på eftersending`); setWaiting(null); }} />}
      {rest && <RestModal batchId={rest} onClose={() => setRest(null)} onDone={(d) => { note(`Batch ${d.number} (${d.department}): eftersending pakket${d.status === 'packed' ? ', batchen er færdig' : ''}`); setRest(null); void qc.invalidateQueries({ queryKey: ['xmas-batch'] }); }} />}
      {kolli && <KolliModal box={kolli} onClose={() => setKolli(null)} onDone={(r) => { note(r.message); setKolli(null); }} />}
      {booked && (
        <Modal title={`${booked.code} er allerede booket`} onClose={() => setBooked(null)}>
          <div className="space-y-3 text-sm">
            <p>{XMAS_PACKAGE_LABELS[booked.kind]} til {booked.department}, fragtbrev {booked.shipment?.consignmentNumber ?? 'uden nummer'}{booked.palletCode ? `, på palle ${booked.palletCode}` : ''}.</p>
            {booked.palletCode && <p className="text-muted">Annullering gælder hele pallen. Den åbnes igen med sine kolli.</p>}
            <div className="flex justify-end gap-2"><button className="btn text-danger mr-auto" disabled={cancel.isPending} onClick={() => cancel.mutate(booked.id)}>{cancel.isPending ? 'Annullerer…' : booked.palletCode ? 'Annullér pallens booking' : 'Annullér booking'}</button><button className="btn" onClick={() => setBooked(null)}>Luk</button><button className="btn btn-primary" autoFocus disabled={reprint.isPending} onClick={() => reprint.mutate(booked.id)}>Print fragtlabel igen</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// --- plans -------------------------------------------------------------------------------------------

export function XmasPlansPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ name: string; ids: string } | null>(null);
  const plans = useQuery({ queryKey: ['xmas-plans'], queryFn: () => get<XmasPlanSummaryDto[]>('/xmas/plans') });
  const create = useMutation({
    mutationFn: (f: { name: string; ids: string }) => post<XmasPlanDto>('/xmas/plans', { name: f.name, shopExternalIds: f.ids.split(/[\s,;]+/).filter(Boolean).map(Number).filter((n) => n > 0) }),
    onSuccess: (p) => { void qc.invalidateQueries({ queryKey: ['xmas-plans'] }); setForm(null); void nav({ to: '/xmas/$id', params: { id: p.id } }); },
    onError: setError,
  });
  return (
    <div className="space-y-4">
      <PageTitle title="Julepakning" subtitle="Gaveshops leveret til mange adresser: én batch pr. afdeling på hver adresse. Pakkebordene arbejder under Scan stregkode." actions={can('xmas.manage') && <button className="btn btn-primary" onClick={() => setForm({ name: '', ids: '' })}>Ny plan</button>} />
      <ErrorBox error={error} />
      {plans.isLoading ? <Spinner /> : (
        <table className="table card p-0 text-sm">
          <thead><tr><th>Plan</th><th>Shops</th><th className="text-right">Adresser</th><th className="text-right">Batches</th><th className="text-right">Gaver</th><th className="text-right">Pakket</th><th className="text-right">Booket</th></tr></thead>
          <tbody>
            {(plans.data ?? []).map((p) => (
              <tr key={p.id} className="cursor-pointer hover:bg-canvas" onClick={() => void nav({ to: '/xmas/$id', params: { id: p.id } })}>
                <td className="font-medium">{p.name}</td>
                <td>{p.shops.map((s) => `${s.name}${s.externalId ? ` (${s.externalId})` : ''}`).join(', ')}</td>
                <td className="text-right tabular-nums">{p.addresses}</td>
                <td className="text-right tabular-nums">{p.batches}</td>
                <td className="text-right tabular-nums">{p.orders}</td>
                <td className="text-right tabular-nums">{p.packedBatches}/{p.batches}</td>
                <td className="text-right tabular-nums">{p.bookedAddresses}/{p.addresses}</td>
              </tr>
            ))}
            {!plans.data?.length && <tr><td colSpan={7} className="text-muted">Ingen planer endnu.</td></tr>}
          </tbody>
        </table>
      )}
      {form && (
        <Modal title="Ny juleplan" onClose={() => setForm(null)}>
          <div className="space-y-3 text-sm">
            <label className="block"><span className="label">Navn</span><input className="input" value={form.name} placeholder="Ballerup Kommune 2026" onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="block"><span className="label">Shops (3x-id, flere adskilt af komma)</span><input className="input" value={form.ids} placeholder="6880" onChange={(e) => setForm({ ...form, ids: e.target.value })} /></label>
            <div className="flex justify-end gap-2"><button className="btn" onClick={() => setForm(null)}>Annuller</button><button className="btn btn-primary" disabled={!form.name.trim() || create.isPending} onClick={() => create.mutate(form)}>Opret</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function XmasPlanPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const nav = useNavigate();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [shopId, setShopId] = useState('');
  const [readyOnly, setReadyOnly] = useState(false);
  const options = useQuery({ queryKey: ['xmas-plan-options'], queryFn: () => get<XmasPlanOptionsDto>('/xmas/plan-options') });
  const plan = useQuery({ queryKey: ['xmas-plan', id], queryFn: () => get<XmasPlanDto>(`/xmas/plans/${id}`) });
  const set = (p: XmasPlanDto) => qc.setQueryData(['xmas-plan', id], p);
  const save = useMutation({ mutationFn: (patch: Partial<XmasPlanDto>) => { const p = { ...plan.data!, ...patch }; return put<XmasPlanDto>(`/xmas/plans/${id}`, { name: p.name, warehouseId: p.warehouseId, locationId: p.locationId, carrierAccountId: p.carrierAccountId, labelPrinterId: p.labelPrinterId, smallLabelPrinterId: p.smallLabelPrinterId, documentPrinterId: p.documentPrinterId }); }, onSuccess: set, onError: setError });
  const addShop = useMutation({ mutationFn: () => post<XmasPlanDto>(`/xmas/plans/${id}/shops`, { externalId: Number(shopId) }), onSuccess: (p) => { set(p); setShopId(''); }, onError: setError });
  const saveSkip = useMutation({ mutationFn: (skus: string[]) => put<XmasPlanDto>(`/xmas/plans/${id}/skip`, { skus }), onSuccess: (x) => { set(x); setError(null); }, onError: setError });
  const saveLater = useMutation({ mutationFn: (skus: string[]) => put<XmasPlanDto>(`/xmas/plans/${id}/later`, { skus }), onSuccess: (x) => { set(x); setError(null); }, onError: setError });
  const printRest = useMutation({ mutationFn: () => post<{ batches: number }>(`/xmas/plans/${id}/print-rest`), onSuccess: (r) => { setMsg(`Restpluklister og medarbejderlabels sendt til print: ${r.batches} batches`); setError(null); }, onError: setError });
  const removeShop = useMutation({ mutationFn: (sid: string) => del<XmasPlanDto>(`/xmas/plans/${id}/shops/${sid}`), onSuccess: set, onError: setError });
  const remove = useMutation({ mutationFn: () => del(`/xmas/plans/${id}`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['xmas-plans'] }); void nav({ to: '/xmas' }); }, onError: setError });
  const build = useMutation({
    mutationFn: () => post<{ added: number; employees: { matched: number; missing: number; error: string | null }; plan: XmasPlanDto }>(`/xmas/plans/${id}/build`),
    onSuccess: (r) => {
      set(r.plan);
      const e = r.employees;
      setMsg(`${r.added} nye gaver lagt i batches. Medarbejdere fra 3x: ${e.matched} fundet${e.missing ? `, ${e.missing} uden login i 3x` : ''}${e.error ? `. 3x svarede ikke: ${e.error}` : ''}`);
      setError(null);
    },
    onError: setError,
  });
  const p = plan.data;
  if (!p) return plan.isLoading ? <Spinner /> : <ErrorBox error={plan.error} />;
  const manage = can('xmas.manage');
  const ready = p.addressList.filter((a) => a.stock?.status === 'ready').length;
  const shown = readyOnly ? p.addressList.filter((a) => a.stock?.status === 'ready') : p.addressList;
  const sel = (value: string | null, options: { id: string; label: string }[], onChange: (v: string | null) => void) => (
    <select className="input" value={value ?? ''} disabled={!manage} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">Vælg</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
  return (
    <div className="space-y-4">
      <PageTitle title={p.name} subtitle={<Link to="/xmas" className="text-primary hover:underline">← Julepakning</Link>} actions={manage && <div className="flex gap-2"><button className="btn text-danger" disabled={remove.isPending} onClick={() => { if (window.confirm(`Slet planen "${p.name}" med alle batches, kasser og paller? Ordrerne røres ikke.`)) remove.mutate(); }}>Slet plan</button><button className="btn btn-primary" disabled={build.isPending} onClick={() => build.mutate()}>{build.isPending ? 'Bygger…' : 'Byg batches'}</button></div>} />
      <ErrorBox error={error} />
      {msg && <div className="rounded-lg bg-success-100 px-3 py-2 text-sm text-success">{msg}</div>}
      <div className="grid grid-cols-6 gap-3">
        {[['Adresser', p.addresses], ['Klar til pakning', p.stockWarehouse ? ready : '–'], ['Batches (afdelinger)', p.batches], ['Gaver', p.orders], ['Pakkede batches', `${p.packedBatches}/${p.batches}`], ['Bookede adresser', `${p.bookedAddresses}/${p.addresses}`]].map(([k, v]) => <div key={k} className="card"><div className="label">{k}</div><div className="text-2xl font-semibold tabular-nums">{v}</div></div>)}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="card space-y-3 text-sm">
          <div className="font-semibold">Indstillinger</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="label">Lager (afsender)</span>{sel(p.warehouseId, options.data?.warehouses ?? [], (v) => save.mutate({ warehouseId: v, locationId: null }))}</label>
            <label className="block"><span className="label">Lokation (hvor varerne tages fra)</span>
              <select className="input" value={p.locationId ?? ''} disabled={!manage || !p.warehouseId} onChange={(e) => save.mutate({ locationId: e.target.value || null })}>
                <option value="">Alle lokationer</option>
                {(options.data?.locations ?? []).filter((l) => l.warehouseId === p.warehouseId).map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </label>
            <label className="block"><span className="label">Fragtkonto</span>{sel(p.carrierAccountId, options.data?.accounts ?? [], (v) => save.mutate({ carrierAccountId: v }))}</label>
            <label className="block"><span className="label">Standard labelprinter 102 × 191 mm</span>{sel(p.labelPrinterId, options.data?.printers ?? [], (v) => save.mutate({ labelPrinterId: v }))}</label>
            <label className="block"><span className="label">Standard labelprinter 76 × 50 mm</span>{sel(p.smallLabelPrinterId, options.data?.printers ?? [], (v) => save.mutate({ smallLabelPrinterId: v }))}</label>
            <label className="block"><span className="label">Standard A4-printer</span>{sel(p.documentPrinterId, options.data?.printers ?? [], (v) => save.mutate({ documentPrinterId: v }))}</label>
          </div>
          <p className="text-xs text-muted">Printerne bruges kun, hvis en bruger ikke har valgt sine egne under Scan stregkode. Fragtkontoen oprettes under Fragt → Konti (Danske Fragtmænd med egen aftale). Indtil der er adgangskoder, kan kontoen stå i testkørsel.</p>
        </div>
        <div className="card space-y-3 text-sm">
          <div className="font-semibold">Shops</div>
          <ul className="divide-y divide-line">
            {p.shops.map((s) => <li key={s.id} className="flex items-center gap-2 py-1.5"><span className="flex-1">{s.name} {s.externalId && <span className="text-muted">({s.externalId})</span>}</span>{manage && <button className="text-muted hover:text-danger" title="Fjern" onClick={() => removeShop.mutate(s.id)}><Trash2 className="h-4 w-4" /></button>}</li>)}
          </ul>
          {manage && <div className="flex gap-2"><input className="input w-40" value={shopId} placeholder="3x-id" onChange={(e) => setShopId(e.target.value.replace(/\D/g, ''))} /><button className="btn" disabled={!shopId || addShop.isPending} onClick={() => addShop.mutate()}>Tilføj shop</button></div>}
          <p className="text-xs text-muted">Shoppens ordrer kommer med, når du trykker "Byg batches". Det kan køres igen: nye ordrer lægges i deres afdelings batch.</p>
        </div>
      </div>
      <div className="card space-y-2 text-sm">
        <div className="font-semibold">Pakkes ikke</div>
        <p className="text-xs text-muted">Varer, der håndteres manuelt (fx donationer). De kommer ikke med på plukliste, følgeseddel, udleveringsliste eller medarbejderlabels, trækkes ikke fra lageret, og en medarbejder, der kun har sådan en vare, tæller ikke som en gave, der skal pakkes.</p>
        <SkuChips skus={p.skipSkus} products={p.products} manage={manage} pending={saveSkip.isPending} onSave={(skus) => saveSkip.mutate(skus)} add="Pakkes ikke" empty="Ingen: alle varer pakkes." />
      </div>
      <div className="card space-y-2 text-sm">
        <div className="font-semibold">Eftersendes</div>
        <p className="text-xs text-muted">Varer, der kommer senere. Pluklister, der printes, mens en vare står her, pakkes uden den: den står på følgeseddel og udleveringsliste som "eftersendes", men plukkes, trækkes og får medarbejderlabel først med restpluklisten. Adressens kasser bookes først, når resten er pakket, medmindre adressen sendes uden. Fjern varen, når den er kommet, så pakkes den med igen på de pluklister, der printes derefter.</p>
        <SkuChips skus={p.laterSkus} products={p.products} manage={manage} pending={saveLater.isPending} onSave={(skus) => saveLater.mutate(skus)} add="Eftersendes" empty="Ingen: alle varer pakkes med det samme." />
        {p.restLines.length > 0 && (
          <>
            <table className="table">
              <thead><tr><th>SKU</th><th>Vare</th><th className="text-right">Mangler at blive pakket</th><th className="text-right">Pakket siden</th><th className="text-right">På lager</th></tr></thead>
              <tbody>
                {p.restLines.map((l) => (
                  <tr key={l.sku}>
                    <td className="font-mono text-xs">{l.sku}</td>
                    <td>{l.title}</td>
                    <td className={`text-right tabular-nums ${l.open ? 'font-semibold' : 'text-muted'}`}>{l.open}</td>
                    <td className="text-right tabular-nums">{l.packed}</td>
                    <td className={`text-right tabular-nums ${l.onHand !== null && l.onHand < l.open ? 'text-danger' : ''}`}>{l.onHand ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap items-center gap-3">
              <span>{p.addressList.filter((a) => a.rest > 0).length} adresser afventer eftersending.</span>
              <button className="btn ml-auto" disabled={printRest.isPending || !p.restLines.some((l) => l.open)} onClick={() => printRest.mutate()}><Printer className="mr-1 inline h-4 w-4" />Print alle restpluklister og medarbejderlabels</button>
            </div>
          </>
        )}
      </div>
      {p.issues.length > 0 && (
        <div className="card text-sm">
          <div className="mb-2 font-semibold">{p.unbatched} gaver er ikke i en batch</div>
          <ul className="max-h-40 space-y-0.5 overflow-auto">{p.issues.map((i) => <li key={i.orderId}>{can('orders.view') ? <Link to="/orders/$id" params={{ id: i.orderId }} className="text-primary hover:underline">{i.orderNumber}</Link> : i.orderNumber} {i.name ?? ''}: <span className="text-muted">{i.reason}</span></li>)}</ul>
        </div>
      )}
      <StockLines lines={p.lines} />
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={readyOnly} onChange={(e) => setReadyOnly(e.target.checked)} />Vis kun adresser, der er klar til pakning</label>
        <span className="text-xs text-muted">{p.stockWarehouse ? `Ud fra beholdningen på ${p.stockWarehouse}. Adresser får varer i nummerorden, så alle "Klar" kan pakkes samtidig.` : 'Vælg et lager på planen for at se, hvad der er klar til pakning.'}</span>
      </div>
      <table className="table card p-0 text-sm">
        <thead><tr><th>#</th><th>Adresse</th><th className="text-right">Afdelinger</th><th className="text-right">Gaver</th><th className="text-right">Pakket</th><th className="text-right">Kasser booket</th><th>Status</th></tr></thead>
        <tbody>
          {shown.map((a) => (
            <tr key={a.id} className="cursor-pointer hover:bg-canvas" onClick={() => void nav({ to: '/xmas/address/$id', params: { id: a.id } })}>
              <td className="tabular-nums">{a.number}</td>
              <td>{a.receiver.street1}, {a.receiver.zip} {a.receiver.city}{a.receiver.company && <div className="text-xs text-muted">{a.receiver.company}</div>}</td>
              <td className="text-right tabular-nums">{a.departments}</td>
              <td className="text-right tabular-nums">{a.orders}</td>
              <td className="text-right tabular-nums">{a.batches.filter((b) => b.status === 'packed').length}/{a.batches.length}</td>
              <td className="text-right tabular-nums">{a.boxesBooked}/{a.boxes}</td>
              <td><StageCell stage={a.stage} stock={a.stock} tracking={a.tracking} />{a.rest > 0 && <div className="text-xs text-amber-700">{a.rest} eftersendes{a.shipWithout ? ' · sendes uden' : ''}</div>}</td>
            </tr>
          ))}
          {!shown.length && <tr><td colSpan={7} className="text-muted">{readyOnly ? 'Ingen adresser er klar til pakning lige nu.' : 'Ingen adresser endnu: tilføj shops og tryk "Byg batches".'}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/** A plan's list of products (by order-line SKU), with a product of its orders to add. */
function SkuChips({ skus, products, manage, pending, onSave, add, empty }: { skus: string[]; products: XmasPlanDto['products']; manage: boolean; pending: boolean; onSave: (skus: string[]) => void; add: string; empty: string }) {
  const [sku, setSku] = useState('');
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {skus.map((s) => {
          const prod = products.find((x) => x.sku.toUpperCase() === s.toUpperCase());
          return (
            <span key={s} className="flex items-center gap-2 rounded-full border border-line bg-canvas px-3 py-1">
              <span className="font-mono text-xs">{s}</span>{prod && <span>{prod.title} · {prod.quantity} stk.</span>}
              {manage && <button className="text-muted hover:text-danger" title="Fjern fra listen" disabled={pending} onClick={() => onSave(skus.filter((x) => x !== s))}><Trash2 className="h-3.5 w-3.5" /></button>}
            </span>
          );
        })}
        {!skus.length && <span className="text-muted">{empty}</span>}
      </div>
      {manage && (
        <div className="flex gap-2">
          <select className="input max-w-md" value={sku} onChange={(e) => setSku(e.target.value)}>
            <option value="">Vælg en vare</option>
            {products.filter((x) => !skus.some((s) => s.toUpperCase() === x.sku.toUpperCase())).map((x) => <option key={x.sku} value={x.sku}>{x.sku} · {x.title} ({x.quantity} stk.)</option>)}
          </select>
          <button className="btn" disabled={!sku || pending} onClick={() => { onSave([...skus, sku]); setSku(''); }}>{add}</button>
        </div>
      )}
    </>
  );
}

/** Per SKU: how much is packed, can be packed from stock, and is missing, as a bar and as numbers. */
function StockLines({ lines }: { lines: XmasStockLineDto[] }) {
  if (!lines.length) return null;
  const known = lines.every((l) => l.packable !== null);
  const miss = (l: XmasStockLineDto) => (l.packable === null ? 0 : l.needed - l.packed - l.packable);
  const sorted = [...lines].sort((a, b) => miss(b) - miss(a) || a.sku.localeCompare(b.sku));
  const sum = (f: (l: XmasStockLineDto) => number) => lines.reduce((t, l) => t + f(l), 0);
  const pct = (n: number, of: number) => `${of ? (n / of) * 100 : 0}%`;
  const Dot = ({ color, label }: { color: string; label: string }) => <span className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-sm ${color}`} />{label}</span>;
  return (
    <div className="card space-y-2 p-0 text-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 pt-3">
        <span className="font-semibold">Varer</span>
        <span className="ml-auto flex flex-wrap gap-3 text-xs text-muted"><Dot color="bg-green-600" label="Pakket" /><Dot color="bg-blue-500" label="Kan pakkes" /><Dot color="bg-red-500" label="Mangler" /></span>
      </div>
      {!known && <p className="px-4 text-xs text-muted">Vælg et lager på planen for at se, hvad der kan pakkes.</p>}
      <table className="table">
        <thead><tr><th>SKU</th><th>Vare</th><th className="w-1/4"></th><th className="text-right">Behov</th><th className="text-right">Kan pakkes</th><th className="text-right">Pakket</th><th className="text-right">Mangler</th></tr></thead>
        <tbody>
          {sorted.map((l) => (
            <tr key={l.sku}>
              <td className="whitespace-nowrap font-mono text-xs">{l.sku}</td>
              <td>{l.title}</td>
              <td title={l.onHand === null ? undefined : `På lager i alt: ${l.onHand}`}>
                <div className="flex h-3 w-full overflow-hidden rounded bg-canvas">
                  <div className="bg-green-600" style={{ width: pct(l.packed, l.needed) }} />
                  <div className="bg-blue-500" style={{ width: pct(l.packable ?? 0, l.needed) }} />
                  <div className="bg-red-500" style={{ width: pct(miss(l), l.needed) }} />
                </div>
              </td>
              <td className="text-right tabular-nums font-medium">{l.needed}</td>
              <td className="text-right tabular-nums">{l.packable ?? '–'}</td>
              <td className="text-right tabular-nums">{l.packed}</td>
              <td className={`text-right tabular-nums ${miss(l) ? 'font-semibold text-danger' : 'text-muted'}`}>{l.packable === null ? '–' : miss(l)}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td colSpan={3}>I alt</td>
            <td className="text-right tabular-nums">{sum((l) => l.needed)}</td>
            <td className="text-right tabular-nums">{known ? sum((l) => l.packable ?? 0) : '–'}</td>
            <td className="text-right tabular-nums">{sum((l) => l.packed)}</td>
            <td className={`text-right tabular-nums ${sum(miss) ? 'text-danger' : 'text-muted'}`}>{known ? sum(miss) : '–'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const STAGE_TONES: Record<XmasAddressStage, 'zinc' | 'green' | 'red' | 'amber' | 'blue'> = { short: 'red', ready: 'green', partly_printed: 'amber', printed: 'blue', partly_packed: 'amber', awaiting_rest: 'amber', packed: 'blue', partly_booked: 'amber', booked: 'green' };

/** How far an address has come, and beside it what stock is missing for the gifts still to be packed. */
function StageCell({ stage, stock, tracking }: { stage: XmasAddressStage | null; stock: XmasAddressDto['stock']; tracking: XmasAddressDto['tracking'] }) {
  const lacking = stock && (stock.status === 'short' || stock.status === 'waiting') ? stock : null;
  const list = lacking?.missing.map((m) => `${m.sku}: skal bruge ${m.needed}, ${lacking.status === 'waiting' ? 'ledigt' : 'på lager'} ${m.available}`) ?? [];
  return (
    <div title={list.join('\n') || undefined}>
      {stage ? <Badge tone={STAGE_TONES[stage]}>{XMAS_ADDRESS_STAGE_LABELS[stage]}</Badge> : <span className="text-xs text-muted">–</span>}
      {stage === 'booked' && tracking.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs">
          {tracking.map((t, i) => (
            <a key={t.url} href={t.url} target="_blank" rel="noopener noreferrer" title={t.consignmentNumber ?? undefined} className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
              Tracking link{tracking.length > 1 ? ` ${i + 1}` : ''}
            </a>
          ))}
        </div>
      )}
      {lacking && (
        <div className="mt-0.5 text-xs text-danger">
          Mangler {lacking.missing.slice(0, 2).map((m) => `${m.needed - m.available} × ${m.sku}`).join(', ')}{lacking.missing.length > 2 ? ` +${lacking.missing.length - 2}` : ''}
          {lacking.status === 'waiting' && <span className="text-muted"> (lageret er brugt af tidligere adresser)</span>}
        </div>
      )}
    </div>
  );
}

/** A weight in kg, saved when the field is left or Enter is pressed. */
function WeightInput({ weightG, onSave }: { weightG: number; onSave: (grams: number) => void }) {
  const [v, setV] = useState(weightG ? String(weightG / 1000).replace('.', ',') : '');
  const save = () => {
    const g = toGrams(v);
    if (g > 0 && g !== weightG) onSave(g);
  };
  return <input className="input py-1" inputMode="decimal" placeholder="kg" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === 'Enter' && save()} />;
}

/** Where a package stands, saved when the field is left or Enter is pressed. */
function PlaceInput({ place, onSave }: { place: string | null; onSave: (place: string | null) => void }) {
  const [v, setV] = useState(place ?? '');
  const save = () => { if ((v.trim().toUpperCase() || null) !== place) onSave(v.trim() || null); };
  return <input className="input py-1 font-mono" placeholder="–" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === 'Enter' && save()} />;
}

const boxState = (b: XmasBoxDto) => (b.shipment ? <Badge tone="green">Booket{b.shipment.consignmentNumber ? ` ${b.shipment.consignmentNumber}` : ''}</Badge> : b.palletCode ? <Badge tone="amber">På åben palle</Badge> : <Badge tone="zinc">Ikke scannet</Badge>);

// --- address: departments, packages, shared pallets ----------------------------------------------------

export function XmasAddressPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const nav = useNavigate();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [edit, setEdit] = useState<XmasAddressDto['receiver'] | null>(null);
  const a = useQuery({ queryKey: ['xmas-address', id], queryFn: () => get<XmasAddressDto>(`/xmas/addresses/${id}`) });
  const set = (d: XmasAddressDto) => { qc.setQueryData(['xmas-address', id], d); setError(null); };
  const print = useMutation({ mutationFn: (what: 'pick' | 'labels' | 'all' | 'rest') => post<{ batches: number }>(`/xmas/addresses/${id}/print`, { what }), onSuccess: (r) => { setMsg(`Sendt til print: ${r.batches} batches`); void a.refetch(); }, onError: setError });
  const shipWithout = useMutation({ mutationFn: (on: boolean) => put<XmasAddressDto>(`/xmas/addresses/${id}/ship-without`, { on }), onSuccess: set, onError: setError });
  const place = useMutation({ mutationFn: ({ box, place }: { box: string; place: string | null }) => put<XmasAddressDto>(`/xmas/boxes/${box}/place`, { place }), onSuccess: set, onError: setError });
  const off = useMutation({ mutationFn: (box: string) => del<XmasAddressDto>(`/xmas/boxes/${box}/pallet`), onSuccess: set, onError: setError });
  const bookPallet = useMutation({ mutationFn: (p: { id: string; size: Size }) => post<XmasAddressDto>(`/xmas/pallets/${p.id}/book`, { size: p.size }), onSuccess: (d) => { set(d); setMsg('Pallen er booket; fragtlabel er sendt til print'); }, onError: setError });
  const reprint = useMutation({ mutationFn: (box: string) => post(`/xmas/boxes/${box}/reprint-freight`), onSuccess: () => setMsg('Fragtlabel sendt til print'), onError: setError });
  const cancelBooking = useMutation({ mutationFn: (box: string) => post<XmasAddressDto>(`/xmas/boxes/${box}/cancel-booking`), onSuccess: (x) => { set(x); setMsg('Bookingen er annulleret. Scan kassen igen for at booke den.'); }, onError: setError });
  const askCancel = (b: XmasBoxDto) => { if (window.confirm(b.palletCode ? `Annullér bookingen af hele pallen ${b.palletCode}? Den åbnes igen med sine kolli.` : `Annullér bookingen af ${b.code}?`)) cancelBooking.mutate(b.id); };
  const weigh = useMutation({ mutationFn: ({ box, weightG }: { box: string; weightG: number }) => put<XmasAddressDto>(`/xmas/boxes/${box}/weight`, { weightG }), onSuccess: set, onError: setError });
  const saveReceiver = useMutation({ mutationFn: (r: XmasAddressDto['receiver']) => put<XmasAddressDto>(`/xmas/addresses/${id}/receiver`, r), onSuccess: (d) => { set(d); setEdit(null); }, onError: setError });
  const d = a.data;
  if (!d) return a.isLoading ? <Spinner /> : <ErrorBox error={a.error} />;
  const open = d.pallets.filter((x) => !x.closedAt);
  return (
    <div className="space-y-4">
      <PageTitle
        title={`Adresse ${d.number}: ${d.receiver.street1}, ${d.receiver.zip} ${d.receiver.city}`}
        subtitle={<button className="text-primary hover:underline" onClick={() => window.history.back()}>← Tilbage</button>}
        actions={<div className="flex gap-2">{d.rest > 0 && <button className="btn" disabled={print.isPending} onClick={() => print.mutate('rest')}><Printer className="mr-1 inline h-4 w-4" />Print restpluklister</button>}<button className="btn" disabled={print.isPending} onClick={() => print.mutate('all')}><Printer className="mr-1 inline h-4 w-4" />Print plukliste og medarbejderlabels for alle afdelinger</button></div>}
      />
      <ErrorBox error={error} />
      {msg && <div className="rounded-lg bg-success-100 px-3 py-2 text-sm text-success">{msg}</div>}
      {d.blockers.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="font-semibold">Mangler</div>
          <ul className="list-disc pl-5">{d.blockers.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="card">
          <div className="mb-1 flex items-center justify-between"><span className="font-semibold">Modtager</span>{can('xmas.manage') && <button className="text-primary hover:underline" onClick={() => setEdit(d.receiver)}>Ret</button>}</div>
          <div>{[d.receiver.company, d.receiver.name, d.receiver.street1, d.receiver.street2, `${d.receiver.zip} ${d.receiver.city}`, d.receiver.phone, d.receiver.email].filter(Boolean).map((l) => <div key={l}>{l}</div>)}</div>
        </div>
        <div className="card">
          <div className="mb-1 font-semibold">Status</div>
          <div className="mb-1"><StageCell stage={d.stage} stock={d.stock} tracking={d.tracking} /></div>
          <div>{d.batches.filter((b) => b.status === 'packed').length} af {d.batches.length} afdelinger pakket · {d.orders} gaver</div>
          <div>{d.boxesBooked} af {d.boxes} kasser booket</div>
          {(d.rest > 0 || d.shipWithout) && (
            <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
              <div>{d.rest > 0 ? `${d.rest} varer eftersendes. ` : ''}{d.shipWithout ? 'Adressen sendes uden at vente: det, der eftersendes, kommer i egne kasser.' : 'Kasserne bookes først, når resten er pakket.'}</div>
              <button className="btn mt-1" disabled={shipWithout.isPending} onClick={() => { if (d.shipWithout || window.confirm('Book adressens kasser nu, uden det der eftersendes? Det sendes senere i egne kasser.')) shipWithout.mutate(!d.shipWithout); }}>{d.shipWithout ? 'Vent alligevel på eftersending' : 'Send uden'}</button>
            </div>
          )}
        </div>
      </div>
      <StockLines lines={d.lines} />
      <table className="table card p-0 text-sm">
        <thead><tr><th>Batch</th><th>Afdeling</th><th className="text-right">Gaver</th><th className="text-right">Pakket</th><th className="text-right">Kasser</th><th>Printet</th><th>Status</th></tr></thead>
        <tbody>
          {d.batches.map((b) => (
            <tr key={b.id} className="cursor-pointer hover:bg-canvas" onClick={() => void nav({ to: '/xmas/batch/$id', params: { id: b.id } })}>
              <td className="font-medium tabular-nums">{b.number}</td>
              <td>{b.department}</td>
              <td className="text-right tabular-nums">{b.orders}{b.cancelled > 0 && <span className="text-xs text-danger"> (+{b.cancelled} annulleret)</span>}{b.skipped > 0 && <span className="text-xs text-muted"> (+{b.skipped} pakkes ikke)</span>}</td>
              <td className="text-right tabular-nums">{b.packed}{b.rest > 0 && <span className="text-xs text-amber-700"> ({b.rest} eftersendes)</span>}</td>
              <td className="text-right tabular-nums">{b.boxes}</td>
              <td className="text-xs">{b.printedAt ? fmtTime(b.printedAt) : ''}</td>
              <td>{b.status === 'packed' ? <Badge tone="green">Pakket</Badge> : b.rest > 0 && b.packed >= b.orders ? <Badge tone="amber">Afventer eftersending</Badge> : <Badge tone="zinc">Åben</Badge>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {open.map((p) => (
        <div key={p.id} className="card space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">Åben palle</span>
            <span className="font-mono text-xs text-muted">{p.code}</span>
            <span>{XMAS_PALLET_SIZE_LABELS[p.size]}, {p.boxes.length} kolli, {kg(p.weightG)}</span>
            <button className="btn btn-primary ml-auto" disabled={bookPallet.isPending} onClick={() => bookPallet.mutate({ id: p.id, size: p.size })}>Book pallen og print label</button>
          </div>
          <ul className="space-y-0.5">
            {p.boxes.map((b) => <li key={b.id} className="flex gap-2"><span className="font-mono text-xs">{b.code}</span><span className="flex-1">{b.department} · {kg(b.weightG)}</span><button className="text-xs text-muted hover:text-danger" onClick={() => off.mutate(b.id)}>Tag af</button></li>)}
          </ul>
        </div>
      ))}
      {d.packages.length > 0 && (
        <table className="table card p-0 text-sm">
          <thead><tr><th>Kasse</th><th>Type</th><th>Batch</th><th>Afdeling</th><th className="w-36">Placering</th><th className="w-32">Vægt (kg)</th><th>Status</th><th className="w-24"></th></tr></thead>
          <tbody>
            {d.packages.map((b) => (
              <tr key={b.id}>
                <td className="font-mono text-xs">{b.code}</td>
                <td>{XMAS_PACKAGE_LABELS[b.kind]}</td>
                <td className="tabular-nums">{b.batchNumber}</td>
                <td>{b.department}</td>
                <td>{b.shipment ? <span className="font-mono text-xs">{b.place ?? ''}</span> : <PlaceInput key={b.place ?? ''} place={b.place} onSave={(p) => place.mutate({ box: b.id, place: p })} />}</td>
                <td>{b.shipment ? kg(b.weightG) : <WeightInput key={b.weightG} weightG={b.weightG} onSave={(weightG) => weigh.mutate({ box: b.id, weightG })} />}</td>
                <td>{boxState(b)}{b.palletCode && b.shipment && <span className="ml-2 font-mono text-xs text-muted">{b.palletCode}</span>}</td>
                <td>{b.shipment && <span className="flex items-center gap-3"><button className="text-muted hover:text-ink" title="Print fragtlabel igen" onClick={() => reprint.mutate(b.id)}><Printer className="h-4 w-4" /></button><button className="text-xs text-muted hover:text-danger" disabled={cancelBooking.isPending} onClick={() => askCancel(b)}>Annullér</button></span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {edit && (
        <Modal title="Modtager" onClose={() => setEdit(null)}>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {(['company', 'name', 'street1', 'street2', 'zip', 'city', 'phone', 'email'] as const).map((k) => (
              <label key={k} className="block"><span className="label">{{ company: 'Firma', name: 'Att.', street1: 'Adresse', street2: 'Adresse 2', zip: 'Postnr.', city: 'By', phone: 'Telefon', email: 'E-mail' }[k]}</span><input className="input" value={edit[k] ?? ''} onChange={(e) => setEdit({ ...edit, [k]: e.target.value || (k === 'street1' || k === 'zip' || k === 'city' ? '' : null) })} /></label>
            ))}
            <div className="col-span-2 flex justify-end gap-2"><button className="btn" onClick={() => setEdit(null)}>Annuller</button><button className="btn btn-primary" disabled={saveReceiver.isPending} onClick={() => saveReceiver.mutate(edit)}>Gem</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// --- batch: pick and pack ----------------------------------------------------------------------------

export function XmasBatchPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [resting, setResting] = useState(false);
  const b = useQuery({ queryKey: ['xmas-batch', id], queryFn: () => get<XmasBatchDto>(`/xmas/batches/${id}`) });
  const set = (d: XmasBatchDto) => { qc.setQueryData(['xmas-batch', id], d); setError(null); };
  const print = useMutation({ mutationFn: (what: 'pick' | 'labels' | 'all' | 'rest') => post(`/xmas/batches/${id}/print`, { what }), onSuccess: () => { setMsg('Sendt til print'); void b.refetch(); }, onError: setError });
  const scan = useMutation({ mutationFn: (code: string) => post<XmasBatchDto>(`/xmas/batches/${id}/scan`, { code }), onSuccess: (d) => { set(d); setMsg(null); }, onError: (e) => { setError(e); setMsg(null); } });
  const toggle = useMutation({ mutationFn: ({ orderId, packed }: { orderId: string; packed: boolean }) => put<XmasBatchDto>(`/xmas/batches/${id}/orders/${orderId}`, { packed }), onSuccess: set, onError: setError });
  const reprint = useMutation({ mutationFn: (boxId: string) => post<XmasBatchDto>(`/xmas/boxes/${boxId}/reprint`), onSuccess: (d) => { set(d); setMsg('Kasselabel sendt til print'); }, onError: setError });
  const delBox = useMutation({ mutationFn: (boxId: string) => del<XmasBatchDto>(`/xmas/boxes/${boxId}`), onSuccess: set, onError: setError });
  const resetPrint = useMutation({ mutationFn: () => post<XmasBatchDto>(`/xmas/batches/${id}/reset-print`), onSuccess: (x) => { set(x); setMsg('Print er nulstillet: batchen står som ikke printet'); }, onError: setError });
  /** This batch's pick list asks about the packages; another's is for the scan station. */
  const onScan = (c: string) => {
    if (b.data && c.toUpperCase() === b.data.restCode) return b.data.rest ? setResting(true) : setError(new Error('Batchen har ingen eftersending tilbage'));
    if (!/^XL\d+$/i.test(c)) return scan.mutate(c);
    if (b.data && c.toUpperCase() === b.data.code) return setAsking(true);
    setError(new Error(`${c} er en anden batchs plukliste: scan den under Scan stregkode`));
  };
  const d = b.data;
  if (!d) return b.isLoading ? <Spinner /> : <ErrorBox error={b.error} />;
  const live = d.gifts.filter((g) => !g.cancelled);
  const where = `Adresse ${d.address.number}: ${d.address.street1}, ${d.address.zip} ${d.address.city}`;
  return (
    <div className="space-y-4">
      {resting && <RestModal batchId={id} onClose={() => setResting(false)} onDone={(x) => { set(x); setResting(false); setMsg('Eftersendingen er pakket'); }} />}
      {asking && <PackagesModal batch={{ ...d, where }} onClose={() => setAsking(false)} onDone={(x) => { set(x); setAsking(false); setMsg('Kasselabels sendt til print'); }} />}
      <PageTitle
        title={`Batch ${d.number}: ${d.department} (${d.code})`}
        subtitle={<Link to="/xmas/address/$id" params={{ id: d.addressId }} className="text-primary hover:underline">← {where}</Link>}
        actions={
          <div className="flex gap-2">
            <button className="btn" onClick={() => print.mutate('pick')}>Print plukliste</button>
            <button className="btn" onClick={() => print.mutate('labels')}>Print medarbejderlabels</button>
          </div>
        }
      />
      <ErrorBox error={error} />
      {msg && <div className="rounded-lg bg-success-100 px-3 py-2 text-sm text-success">{msg}</div>}
      <div className="card flex flex-wrap items-center gap-4">
        <ScanField placeholder="Scan ordrenummer, brugernavn eller plukliste" busy={scan.isPending || asking || resting} onScan={onScan} />
        <div className="text-lg font-semibold tabular-nums">{d.packed} / {d.orders} pakket</div>
        {d.status === 'packed' && <Badge tone="green">Færdig</Badge>}
        {d.rest > 0 && <Badge tone="amber">{d.rest} eftersendes</Badge>}
        <div className="ml-auto flex items-center gap-2 text-sm">
          {d.printedAt ? <><span className="text-muted">Plukliste printet {fmtTime(d.printedAt)}</span><button className="btn" disabled={resetPrint.isPending} onClick={() => resetPrint.mutate()}>Nulstil print</button></> : <span className="text-muted">Plukliste ikke printet</span>}
        </div>
      </div>
      <StockLines lines={d.lines} />
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <table className="table card p-0 text-sm">
            <thead><tr><th className="w-10"></th><th>Medarbejder</th><th>Leder</th><th>Gave</th><th>Ordre</th></tr></thead>
            <tbody>
              {d.gifts.map((g) => (
                <tr key={`${g.orderId}-${g.sku}`} className={g.cancelled || g.skipped ? 'opacity-50' : g.packedAt ? 'bg-success-100/40' : ''}>
                  <td><input type="checkbox" disabled={g.cancelled} checked={!!g.packedAt} onChange={(e) => toggle.mutate({ orderId: g.orderId, packed: e.target.checked })} /></td>
                  <td>{g.name}{g.username && <span className="ml-2 font-mono text-xs text-muted">{g.username}</span>}{g.cancelled && <span className="ml-2 text-xs text-danger">annulleret</span>}{g.skipped && <span className="ml-2 text-xs text-muted">pakkes ikke</span>}</td>
                  <td>{g.leader}</td>
                  <td>{g.quantity > 1 && `${g.quantity} × `}{g.title} <span className="font-mono text-xs text-muted">{g.sku}</span>{g.later && <span className="ml-2 text-xs text-amber-700">{g.later === 'packed' ? 'eftersendt' : 'eftersendes'}</span>}</td>
                  <td className="font-mono text-xs">{g.orderNumber}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-4">
          {d.restPick.length > 0 && (
            <div className="card space-y-2 text-sm">
              <div className="font-semibold">Eftersendes <span className="font-mono text-xs font-normal text-muted">{d.restCode}</span></div>
              <ul className="space-y-1">{d.restPick.map((p) => <li key={p.sku} className="flex gap-2"><span className="w-8 text-right font-semibold tabular-nums">{p.quantity}</span><span className="flex-1">{p.title}{p.variant && <span className="block font-medium">{p.variant}</span>}</span><span className="font-mono text-xs text-muted">{p.sku}</span></li>)}</ul>
              <div className="flex gap-2"><button className="btn" disabled={print.isPending} onClick={() => print.mutate('rest')}>Print restplukliste</button><button className="btn btn-primary" onClick={() => setResting(true)}>Resten er pakket</button></div>
            </div>
          )}
          <div className="card text-sm">
            <div className="mb-2 font-semibold">Plukliste</div>
            <ul className="space-y-1">
              {d.pick.map((p) => p.parts ? (
                <li key={p.sku} className="rounded bg-canvas p-1.5">
                  <div className="flex gap-2 font-medium"><span className="flex-1">{p.title}</span><span className="text-xs font-normal text-muted">{p.quantity} sampak</span></div>
                  <div className="mb-1 break-all font-mono text-xs text-muted">{p.sku}</div>
                  <ul className="space-y-0.5">{p.parts.map((x) => <li key={x.sku} className="flex gap-2"><span className="w-8 text-right font-semibold tabular-nums">{x.quantity}</span><span className="flex-1">{x.title}</span><span className="font-mono text-xs text-muted">{x.sku}</span></li>)}</ul>
                </li>
              ) : <li key={p.sku} className="flex gap-2"><span className="w-8 text-right font-semibold tabular-nums">{p.quantity}</span><span className="flex-1">{p.title}{p.variant && <span className="block font-medium">{p.variant}</span>}</span><span className="font-mono text-xs text-muted">{p.sku}</span></li>)}
            </ul>
            <div className="mt-2 text-xs text-muted">{live.length} gaver</div>
          </div>
          <div className="card space-y-2 text-sm">
            <div className="flex items-center justify-between"><span className="font-semibold">Kasser</span><button className="btn" onClick={() => setAsking(true)}>Angiv paller og kolli</button></div>
            <ul className="space-y-1">
              {d.boxList.map((x) => (
                <li key={x.id} className="flex items-center gap-2">
                  <span className="font-mono text-xs">{x.code}</span>
                  <span className="flex-1">{XMAS_PACKAGE_LABELS[x.kind]}, {kg(x.weightG)}{x.shipment ? <span className="text-muted"> · booket</span> : x.palletCode ? <span className="text-muted"> · på {x.palletCode}</span> : ''}</span>
                  <button className="text-muted hover:text-ink" title="Print kasselabel igen" onClick={() => reprint.mutate(x.id)}><Printer className="h-4 w-4" /></button>
                  {!x.shipment && <button className="text-muted hover:text-danger" title="Slet kasse" onClick={() => delBox.mutate(x.id)}><Trash2 className="h-4 w-4" /></button>}
                </li>
              ))}
              {!d.boxList.length && <li className="text-muted">Ingen kasser endnu: scan pluklisten, når batchen er pakket.</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
